import React, { useRef, useMemo } from 'react';
import { Typography, Button, Box} from '@mui/material';
import {
	WillContainer,
	WillHeader,
	SectionHeader,
	Paragraph,
	IndentedParagraph,
	StyledList,
	StyledListItem,
	SignatureBlockContainer,
	SignatureContent,
	BarcodeContainer,
	BarcodeValue,
	BarcodeLines,
	BarcodeLine,
	SignatureLinesContainer,
	PageBreak,
	ResultPageContainer,
	ActionButtonGroup
} from './WillDocumentDisplay.styles';

export default function WillDocumentDisplay({ formData, handleBack }) {
	const willContainerRef = useRef(null);

	// Calculate Barcode Value Once
	const barcodeValue = useMemo(() => {
		return `W${formData.idNumber?.substring(0, 4) || "0000"}-${Math.floor(
			Math.random() * 10000,
		)
			.toString()
			.padStart(4, "0")}`;
	}, [formData.idNumber]);

	const numberToWords = (num) => {
		const words = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
		const tens = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];

		if (isNaN(num) || num < 0) return num.toString();

		if (num < 20) return words[num];
		if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? "-" + words[num % 10] : "");
		if (num === 100) return "one hundred";

		return num.toString();
	};

	// Returns only the inner content, to be placed inside a <td>
	const SignatureBlockContent = ({ barcodeValue }) => (
		<SignatureContent className="signature-content">
			<SignatureLinesContainer className="signature-lines-container">
				<Typography component="p">Testator/Testatrix Signature: __________________ Date: ______________</Typography>
				<Typography component="p">Witness 1 Signature: _________________________ Date: ______________</Typography>
				<Typography component="p">Witness 2 Signature: _________________________ Date: ______________</Typography>
			</SignatureLinesContainer>
			<BarcodeContainer className="barcode-container">
				<BarcodeValue variant="caption" className="barcode-value">{barcodeValue}</BarcodeValue>
				<BarcodeLines className="barcode-lines">
					{Array(30).fill().map((_, i) => (
						<BarcodeLine key={i} className="barcode-line" style={{ height: `${20 + Math.random() * 15}px` }} />
					))}
				</BarcodeLines>
			</BarcodeContainer>
		</SignatureContent>
	);

	const generateWillContent = () => {
		let relationshipText = "";

		switch (formData.maritalStatus) {
			case "single":
				relationshipText = "being single";
				break;
			case "married":
				relationshipText = `married ${formData.marriageType}`;
				break;
			case "divorced":
				relationshipText = "divorced";
				break;
			case "widowed":
				relationshipText = "widowed";
				break;
			case "customary":
				relationshipText = "in a customary/religious marriage";
				break;
			default:
				relationshipText = "";
		}

		return (
			<WillContainer ref={willContainerRef}>
				<table style={{ width: '100%', borderCollapse: 'collapse', borderSpacing: 0 }}>
					<tfoot className="print-footer-signature">
						<tr>
							<td style={{ padding: 0 }}>
								<SignatureBlockContent barcodeValue={barcodeValue} />
							</td>
						</tr>
					</tfoot>

					<tbody>
						<tr>
							<td style={{ padding: 0 }}>
								<WillHeader variant="h2" component="h2">Last Will and Testament</WillHeader>
								<Paragraph>
									This is the last will of {formData.firstName} {formData.lastName},
									Identity Number {formData.idNumber}, presently of {formData.address}{" "}
									and {relationshipText}.
								</Paragraph>

								<SectionHeader variant="h4" component="h4">1. Revocation</SectionHeader>
								<Paragraph>
									I hereby revoke all wills, codicils and other testamentary acts
									previously executed by me and declare this to be my last will.
								</Paragraph>

								<SectionHeader variant="h4" component="h4">2. Definition</SectionHeader>
								<Paragraph>In this my will, unless the contrary is clearly intended:</Paragraph>
								<IndentedParagraph>
									2.1 the one gender shall include the others and the singular shall
									include the plural and vice versa;
								</IndentedParagraph>
								<IndentedParagraph>
									2.2 "my trust estate" means the trust created in this will comprising
									of my estate;
								</IndentedParagraph>
								<IndentedParagraph>2.3 "issue" means lawful issue;</IndentedParagraph>

								{formData.maritalStatus === "married" && (
									<IndentedParagraph>
										2.4 "Spouse" means {formData.spouseFirstName}{" "}
										{formData.spouseLastName}.
									</IndentedParagraph>
								)}

								{formData.hasChildren && formData.children.length > 0 && (
									<>
										<IndentedParagraph>2.{formData.maritalStatus === "married" ? 5 : 4} "Children" means my children:</IndentedParagraph>
										<StyledList>
											{formData.children.map((child, index) => (
												<StyledListItem key={index}>
													{child.name}, born on{" "}
													{new Date(child.dateOfBirth).toLocaleDateString('en-ZA')}
												</StyledListItem>
											))}
										</StyledList>
									</>
								)}

								<SectionHeader variant="h4" component="h4">3. Appointment and Power of Executor and Administrator</SectionHeader>
								<Paragraph>
									3.1 I appoint the following persons jointly to be the executors of
									this my will and administrators of my estate, hereby giving and
									granting to them all such powers and authority as is allowed in law,
									especially the power of assumption:-
								</Paragraph>
								<IndentedParagraph>
									3.1.1 {formData.executor1FirstName} {formData.executor1LastName}{" "}
									{formData.executor1IdNumber} and
								</IndentedParagraph>
								<IndentedParagraph>
									3.1.2 {formData.executor2FirstName} {formData.executor2LastName}{" "}
									{formData.executor2IdNumber}.
								</IndentedParagraph>

								<Paragraph>
									3.2 Should any of the persons nominated in 3.1 fail to accept their
									appointments as executor and/or administrator, relinquish such
									appointments or die, then I direct that the remaining executors and/or
									administrators may continue to act alone, provided, however, that
									there shall at all times be no fewer than two (2) executors and/or
									administrators in office as such except for the purpose of assuming or
									otherwise appointing a second executor and/or administrator where for
									any reason whatsoever, there shall be only one executor and/or
									administrator in office.
								</Paragraph>

								<Paragraph>
									3.3 A decision of a majority of my executors and administrators shall
									be deemed to be a decision of them all.
								</Paragraph>

								<Paragraph>
									3.4 It shall not be necessary for any executor or administrator to
									furnish security to the Master of the High Court or any other
									authority whatsoever for the due administration of my estate, whether
									under the Administration of Estates Act, the Trust Property Control
									Act or any other law already enacted or which may hereafter be passed
									in connection with the granting of security by any executor or
									administrator.
								</Paragraph>

								<Paragraph>
									3.5 Any executor and administrator may undertake such specialised
									duties on behalf of my estate and all beneficiaries as would normally
									fall within his field of professional activities, if any, and in
									addition to normal executor's and administrator's remuneration, he may
									receive from my estate the usual fees which he would charge for his
									professional services had he not been appointed as executor and
									administrator in terms of this will.
								</Paragraph>

								<SectionHeader variant="h4" component="h4">4. Disposal of Estate</SectionHeader>

								{formData.estateItems.length > 0 && (
									<>
										<Paragraph sx={{ fontWeight: 'bold', textTransform: 'uppercase', mt: 2, mb: 1 }}>4.1 Special Bequests</Paragraph>

										{formData.estateItems.map((item, index) => (
											<Box key={item.id || index} mb={1.5}>
												{item.type === "immovable" && (
													<Paragraph>
														4.1.{index + 1} I bequeath to {item.beneficiaries}, which
														they shall be entitled to receive unencumbered, the
														immovable property situate at {item.address} ("the
														property") in equal shares
														{item.hasLifelongUsufruct && item.usufructBeneficiary ? (
															<>
																, subject to the following conditions:-
																<IndentedParagraph component="span" sx={{display: 'block', mt: 0.5}}>
																	(a) {item.usufructBeneficiary} shall have a lifelong
																	usufruct over the property until such time as the
																	property is sold alternatively until his/her death;
																</IndentedParagraph>
																{item.specialConditions && (
																	<IndentedParagraph component="span" sx={{display: 'block'}}> (b) {item.specialConditions}; </IndentedParagraph>
																)}
																<IndentedParagraph component="span" sx={{display: 'block'}}>
																	({item.specialConditions ? "c" : "b"}) all costs of
																	transfer of the property into their name shall be
																	borne by my estate.
																</IndentedParagraph>
															</>
														) : '.'}
													</Paragraph>
												)}

												{item.type === "movable" && (
													<Paragraph>
														4.1.{index + 1} I bequeath to {item.beneficiaries}, the
														proceeds of all and any {item.description} owned by me as at
														the date of my death.
													</Paragraph>
												)}

												{item.type === "cash" && (
													<Paragraph>
														4.1.{index + 1} I bequeath {item.amount} ({item.amountInWords})
														to {item.beneficiary}
														{item.purpose && (
															<>, to be utilised specifically for the {item.purpose}</>
														)}
														{item.alternativeBeneficiary && (
															<>. In the event that this beneficiary predeceases me or is unable to inherit,
																then I bequeath that the amount of {item.amount} devolve
															to {item.alternativeBeneficiary}</>
														)}
														.
													</Paragraph>
												)}

												{item.type === "business" && (
													<>
														<Paragraph sx={{ fontWeight: 'bold', textTransform: 'uppercase', mb: 0.5 }}>{item.companyName}</Paragraph>
														<Paragraph>
															4.1.{index + 1} For the sake of good order I record that:
														</Paragraph>
														<IndentedParagraph>
															4.1.{index + 1}.1 I am a {item.sharePercentage}{" "}
															shareholder in {item.companyName} (Registration Number:{" "}
															{item.registrationNumber}) ("the company");
														</IndentedParagraph>
														<IndentedParagraph>
															4.1.{index + 1}.2 There is currently{" "}
															{item.hasAgreement ? "" : "no"} agreement in place
															detailing the distribution of my shares or equity in the
															company upon my death;
														</IndentedParagraph>
														{!item.hasAgreement && (
															<IndentedParagraph>
																4.1.{index + 1}.3 Until such time as a valid shareholders' agreement is
																entered into which supersedes my wishes herein, any shares or equity
																owned by me in the company at the time of my death shall
																devolve to {item.beneficiary}.
															</IndentedParagraph>
														)}
													</>
												)}

												{item.type === "trust" && (
													<Paragraph>
														4.1.{index + 1} I direct that the {item.trustType} known as {item.trustName} shall benefit {item.beneficiaries} subject to the following conditions: {item.conditions}.
													</Paragraph>
												)}

												{item.type === "digital" && (
													<Paragraph>
														4.1.{index + 1} I bequeath the digital asset described as [{item.assetType}: {item.description}] to {item.beneficiary}. Access information can be found {item.accessInformation}.
													</Paragraph>
												)}

												{item.type === "personal" && (
													<Paragraph>
														4.1.{index + 1} I bequeath the personal item described as "{item.itemDescription}"{item.sentimentalValue ? `, noted for its sentimental value relating to ${item.sentimentalValue},` : ''} to {item.beneficiary}.
													</Paragraph>
												)}

												{item.type === "policy" && index === 0 && (
													<>
														<Paragraph sx={{ fontWeight: 'bold', textTransform: 'uppercase', mt: 2, mb: 1 }}>4.2 Policies, Investments and Shares</Paragraph>
														<Paragraph>
															4.2.1 The beneficiaries of my insurance and other policies
															shall be consistent with the beneficiaries as nominated
															directly with the respective institutions providing such policies.
														</Paragraph>
														<Paragraph>
															4.2.2 For the sake of good order and to the best
															of my knowledge, I record that as at the date of this
															will, my assets include the following policies/investments:
														</Paragraph>
													</>
												)}

												{item.type === "policy" && (
													<IndentedParagraph>
														4.2.2.{index + 1} {item.provider} {item.policyType}
														{item.policyNumber && <> (Policy Number: {item.policyNumber})</>}
														{item.accountNumber && <> (Account Number: {item.accountNumber})</>}.
													</IndentedParagraph>
												)}
											</Box>
										))}
									</>
								)}

								{formData.remainderBeneficiaries.length > 0 && (
									<>
										<Paragraph sx={{ fontWeight: 'bold', textTransform: 'uppercase', mt: 2, mb: 1 }}>
											4.3 Remainder of Estate
										</Paragraph>
										<Paragraph>
											I bequeath the remainder of my estate and effects, after settling all debts, taxes, and specific bequests mentioned above, as follows:-
										</Paragraph>
										{formData.remainderBeneficiaries.map((ben, index) => (
											<IndentedParagraph key={ben.id || index}>
												{formData.estateItems.length > 0 ? '4.3' : '4.1'}.{index + 1} {ben.percentage}% (
												{numberToWords(parseInt(ben.percentage, 10))}
												{" percent"}) to {ben.name}
												{ben.relationship ? ` (${ben.relationship})` : ""}.
											</IndentedParagraph>
										))}
									</>
								)}

								{formData.hasMinorChildren && (
									<>
										<SectionHeader variant="h4" component="h4">5. Children's Trusts</SectionHeader>
										<Paragraph>
											5.1 Should any beneficiary named herein be under the age of {formData.trustAge || 25} at the time of my death, their inheritance shall be held in trust by my appointed Trustees under the terms outlined below.
										</Paragraph>
										<Paragraph>
											5.2 My Trustees shall establish a separate trust for each minor beneficiary. The capital and income of each trust shall be managed for the benefit of the respective beneficiary.
										</Paragraph>
										<Paragraph>
											5.3 My Trustees are empowered to use the income and, if necessary, the capital of the trust for the maintenance, education, and general welfare of the beneficiary in a manner they deem appropriate. Any unused income may be accumulated and added to the capital.
										</Paragraph>
										<Paragraph>
											5.4 Upon reaching the age of {formData.trustAge || 25}, the beneficiary shall receive the remaining capital and accumulated income of their trust outright.
										</Paragraph>
										<Paragraph>
											5.5 If a beneficiary passes away before reaching the age of {formData.trustAge || 25}, the remaining trust assets shall devolve upon their lawful issue per stirpes. If they leave no issue, the assets shall be distributed among the remaining beneficiaries of my estate as if the deceased beneficiary had predeceased me without issue.
										</Paragraph>

										<SectionHeader variant="h4" component="h4">6. Directives to Trustees</SectionHeader>
										<Paragraph>I direct that my Trustees shall have the following powers, in addition to those conferred by law:</Paragraph>
										<IndentedParagraph>
											6.1 To invest trust funds in any manner they deem prudent, with the power to vary investments.
										</IndentedParagraph>
										<IndentedParagraph>
											6.2 To make payments for the benefit of a minor beneficiary to their legal guardian or directly for expenses related to their upbringing and education.
										</IndentedParagraph>
										<IndentedParagraph>
											6.3 To engage professional advisors (legal, financial) as needed, with fees payable from the trust assets.
										</IndentedParagraph>
										<IndentedParagraph>
											6.4 To make decisions by majority if multiple Trustees are acting.
										</IndentedParagraph>

										<SectionHeader variant="h4" component="h4">7. Distribution in Kind</SectionHeader>
										<Paragraph>
											My Executors and Trustees may, in their discretion, when making any distribution, distribute assets in kind (i.e., the actual asset) or realise assets and distribute the cash proceeds, or a combination thereof.
										</Paragraph>

										<SectionHeader variant="h4" component="h4">8. Executor's and Administrator's Powers</SectionHeader>
										<Paragraph>
											In addition to powers granted by law, my Executors and Administrators (Trustees) shall have full authority to manage my estate and any trusts, including but not limited to: selling, leasing, or mortgaging property; operating businesses; settling debts; investing funds; instituting or defending legal actions; and distributing the estate according to this Will. They shall act honestly and in good faith and shall not be required to furnish security unless mandated by law.
										</Paragraph>

										<SectionHeader variant="h4" component="h4">9. Estate Duty and Taxes</SectionHeader>
										<Paragraph>
											All estate duties, inheritance taxes, and other governmental charges payable upon my death with respect to property comprising my gross estate for tax purposes shall be paid out of the residue of my estate, without apportionment or reimbursement from any beneficiary, unless otherwise specified herein regarding a particular asset. Beneficiaries shall not be required to collate (bring into account) any gifts received from me during my lifetime.
										</Paragraph>

										<SectionHeader variant="h4" component="h4">10. Insolvency or Cession of Benefits</SectionHeader>
										<Paragraph>
											Should any beneficiary attempt to cede, pledge, assign, or otherwise encumber their interest under this Will before distribution, or should their interest become subject to attachment or insolvency proceedings, such beneficiary's right to inherit shall immediately cease. My Trustees shall, in such event, have the absolute discretion to apply the forfeited share for the benefit of the beneficiary and/or their dependents, or to distribute it as if the beneficiary had predeceased me without issue.
										</Paragraph>

										<SectionHeader variant="h4" component="h4">11. Exclusion from Marital Regimes</SectionHeader>
										<Paragraph>
											Any benefit accruing to any beneficiary under this Will shall be excluded from any community of property, community of profit and loss, or accrual sharing regime that may exist between the beneficiary and their spouse. Such benefits shall form the separate property of the beneficiary and shall not be subject to any marital power of a spouse.
										</Paragraph>
									</>
								)}
							</td>
						</tr>
					</tbody>
				</table>
			</WillContainer>
		);
	};

	const handlePrint = () => {
		if (willContainerRef.current) {
			const printContent = willContainerRef.current.innerHTML;

			const printStyles = `
				@page { 
					margin: 2cm; 
					margin-bottom: 0cm;
					size: A4; 
				}

				body { 
					font-family: "Times New Roman", Times, serif; 
					visibility: hidden; 
				}

				@media print {
					body { 
						visibility: visible; 
						margin: 0; 
						padding: 0; 
					}

					table {
						width: 100%;
						border-collapse: collapse;
						border-spacing: 0;
						page-break-inside: auto; 
					}
					tbody { 
						display: block; 
					}
					tr { 
						page-break-inside: auto; 
						page-break-after: auto;
					}
					td {
						padding: 0; 
						page-break-inside: auto; 
					}

					h2 { 
						text-align: center; font-weight: bold; margin-bottom: 1.5em; 
						text-transform: uppercase; font-size: 1.8rem; 
						break-after: avoid-page; 
					}
					h4 { 
						font-weight: bold; margin-top: 1.5em; margin-bottom: 0.8em; 
						text-transform: uppercase; font-size: 1.1rem;
						break-after: avoid-page; break-before: auto; 
					}
					p, div { 
						text-align: justify; margin-bottom: 0.8em; line-height: 1.6;
						widows: 3; orphans: 3; 
						page-break-inside: auto;
					}
					td p[style*="margin-left"], td div[style*="margin-left"] > p, td .MuiTypography-root[style*="margin-left"] { 
						margin-left: 40px !important; 
					}
					ul { 
						margin-left: 40px; padding-left: 20px;
						list-style-type: disc; margin-bottom: 0.8em;
					}
					li { 
						display: list-item; padding-top: 2px; padding-bottom: 2px;
						margin-bottom: 4px; 
					}
					.action-button-group-class, 
					.MuiButtonBase-root { 
						display: none !important; 
					}

					tfoot.print-footer-signature {
						display: table-footer-group; 
						width: 100%; 
						page-break-inside: avoid; 
					}
					tfoot.print-footer-signature td {
						border-top: 1px dashed #ccc !important; 
						padding-top: 10px !important; 
						padding-bottom: 10px !important; 
						width: 100%; 
					}

					.print-footer-signature .signature-content { 
						display: flex !important;
						align-items: flex-end !important;
						justify-content: space-between !important;
						gap: 30px !important;
						width: 100%; 
					}
					.print-footer-signature .barcode-container { 
						display: flex !important;
						flex-direction: column !important;
						align-items: center !important;
						min-width: 150px !important;
						flex-shrink: 0 !important; 
					}
					.print-footer-signature .barcode-value { 
						font-family: monospace !important; 
						font-size: 8pt !important; 
						letter-spacing: 1px !important;
						margin-bottom: 4px !important; 
						color: #000 !important; 
					}
					.print-footer-signature .barcode-lines { 
						display: flex !important;
						align-items: flex-end !important;
						height: 35px !important; 
						overflow: hidden !important;
						color: black !important;
					}
					.print-footer-signature .barcode-line { 
						width: 1.5px !important;           
						background-color: #000 !important; 
						margin: 0 0.5px !important;  
						display: inline-block !important;   
						height: 30px !important;    
						flex-shrink: 0 !important;
					}
					.print-footer-signature .signature-lines-container { 
						flex-grow: 1 !important;
						text-align: left !important;
						padding-right: 15px !important; 
					}
					.print-footer-signature .signature-lines-container p { 
						margin: 6px 0 !important;     
						font-size: 10pt !important;    
						line-height: 1.8 !important; 
						color: #000 !important;
						font-family: "Times New Roman", Times, serif !important; 
						white-space: nowrap !important; 
					}
					
				} 
			`;

			const printWindow = window.open('', '_blank', 'height=800,width=800');

			if (printWindow) {
				printWindow.document.open();
				printWindow.document.write(`
					<!DOCTYPE html>
					<html>
						<head>
							<title>Print Will</title>
							<style>${printStyles}</style>
						</head>
						<body>
							${printContent} 
						</body>
					</html>
				`);
				printWindow.document.close();

				let printJobFinished = false;
				const closeWindowIfNeeded = () => {
					if (!printJobFinished) {
						printJobFinished = true;
						setTimeout(() => { 
							if (!printWindow.closed) {
								printWindow.close(); 
							}
						}, 50); 
					}
				};

				printWindow.onafterprint = closeWindowIfNeeded;
				printWindow.onbeforeunload = () => { printJobFinished = true; };

				setTimeout(() => {
					try {
						printWindow.focus();
						printWindow.print(); 
					} catch (error) {
						console.error("Error initiating print:", error);
						alert('Could not initiate print dialog. Please try again.');
						closeWindowIfNeeded();
					}
				}, 500);

			} else {
				alert('Could not open print window. Please check your popup blocker settings.');
			}
		}
	};

	return (
		<ResultPageContainer>
			<Typography variant="h4" component="h1" gutterBottom>Your Will & Testament</Typography>
			{generateWillContent()}
			<ActionButtonGroup className="action-button-group-class">
				<Button variant="outlined" onClick={handleBack}>Back to Edit</Button>
				<Button variant="contained" color="secondary" onClick={handlePrint}>Print Will</Button>
			</ActionButtonGroup>
		</ResultPageContainer>
	);
} 